import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import StatsBar from "../components/StatsBar";
import Clients from "../components/Clients";
import CtaBanner from "../components/CtaBanner";
import CompanyVideoEmbed, { getCompanyVideoEmbedUrl } from "../components/CompanyVideoEmbed";
import { useAbout, usePageBanner, useTeamMembers } from "../api/queries";
import HtmlContent from "../components/HtmlContent";
import { Award, Building2, Users, BadgeCheck, MapPin, Download } from "lucide-react";
import { type, typeRichDescription } from "../lib/typography";
import missionIcon from "../assets/mission.png";
import visionIcon from "../assets/vision.png";
import valuesIcon from "../assets/values.png";

const STATIC_VALUE_ICONS = {
  mission: missionIcon,
  vision: visionIcon,
  values: valuesIcon,
};

function resolveStaticValueIcon(title) {
  const normalized = (title || "").trim().toLowerCase();
  if (normalized.includes("mission")) return STATIC_VALUE_ICONS.mission;
  if (normalized.includes("vision")) return STATIC_VALUE_ICONS.vision;
  if (normalized.includes("value")) return STATIC_VALUE_ICONS.values;
  return null;
}

function MissionValueCard({ title, desc }) {
  const displayImage = resolveStaticValueIcon(title);

  return (
    <div className="group p-8 bg-white rounded-xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-2xl transition-all duration-500">
      <div className="w-14 h-14 rounded-lg bg-[#1e6fcf]/10 group-hover:bg-[#1e6fcf] flex items-center justify-center transition-all duration-300">
        {displayImage && (
          <img
            src={displayImage}
            alt=""
            className="w-7 h-7 object-contain transition-all duration-300 group-hover:brightness-0 group-hover:invert"
            loading="lazy"
          />
        )}
      </div>
      <h3 className={`mt-6 ${type.cardTitle} text-[#0a1a2f]`}>{title}</h3>
      <HtmlContent html={desc} className={`mt-3 ${typeRichDescription}`} />
      <div className="mt-6 w-10 h-0.5 bg-[#1e6fcf] group-hover:w-20 transition-all duration-500" />
    </div>
  );
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function isPlaceholderPhoto(url) {
  if (!url) return true;
  return /user\.png|placeholder|default-avatar|no-image|avatar\.png/i.test(url);
}

function TeamMemberInitials({ name }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-[#e8f2fc] to-slate-100">
      <div className="w-11 h-11 rounded-full bg-white border border-[#1e6fcf]/20 shadow-sm flex items-center justify-center">
        <span className="text-sm font-bold text-[#1758a8]">{getInitials(name)}</span>
      </div>
    </div>
  );
}

function TeamMemberPhoto({ member }) {
  const [imageError, setImageError] = useState(false);
  const showPhoto = Boolean(member.photo) && !isPlaceholderPhoto(member.photo) && !imageError;

  if (!showPhoto) {
    return <TeamMemberInitials name={member.name} />;
  }

  return (
    <img
      src={member.photo}
      alt={member.name}
      onError={() => setImageError(true)}
      className="absolute inset-0 block h-full w-full object-cover object-top"
      loading="lazy"
      decoding="async"
    />
  );
}

function LeadershipStat({ icon: Icon, value, label }) {
  return (
    <div className="group relative overflow-hidden rounded-xl bg-white border border-slate-200/80 p-3 sm:p-5 shadow-sm hover:shadow-lg hover:border-[#1e6fcf]/30 transition-all duration-300">
      <div className="absolute top-0 right-0 h-10 w-10 sm:h-14 sm:w-14 rounded-bl-[1.5rem] sm:rounded-bl-[2rem] bg-[#1e6fcf]/[0.06] transition-colors group-hover:bg-[#1e6fcf]/10" />
      <div className="relative flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-[#e8f2fc] group-hover:bg-[#1e6fcf] transition-colors duration-300">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-[#1e6fcf] group-hover:text-white transition-colors duration-300" />
      </div>
      <div className="relative mt-2 sm:mt-3 text-lg sm:text-xl font-extrabold tracking-tight text-[#0a1a2f] leading-none">
        {value}
      </div>
      <div className={`relative mt-1 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500`}>
        {label}
      </div>
    </div>
  );
}

function TeamMemberCard({ member }) {
  return (
    <div className="group min-w-0 max-w-[220px] w-full mx-auto bg-white rounded-lg border border-slate-200 overflow-hidden hover:border-[#1e6fcf] hover:shadow-md transition-all duration-300">
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#e8f2fc]">
        <TeamMemberPhoto member={member} />
      </div>
      <div className="px-3 py-3 md:px-4 md:py-3.5 text-center border-t border-slate-100">
        <h3 className="text-sm md:text-base font-bold text-[#0a1a2f] tracking-tight group-hover:text-[#1758a8] transition-colors line-clamp-2">
          {member.name}
        </h3>
        {member.designation && (
          <p className="mt-1 text-xs md:text-sm font-medium text-[#1758a8] leading-snug line-clamp-2">
            {member.designation}
          </p>
        )}
      </div>
    </div>
  );
}

function toMemberCard(founder) {
  return {
    id: founder.id,
    name: founder.name,
    designation: founder.designation,
    photo: founder.imageUrl,
  };
}

function BrandStorySection({ about }) {
  const credentials = [
    "ISO 9001:2015 Certified",
    "MSME Registered",
    "PAN India Operations",
  ];

  return (
    <section className="border-y border-slate-100 bg-slate-50 py-16 md:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <SectionTitle eyebrow={about.sectionTagline} title={about.mainTitle} />
            <ul className="mt-8 space-y-3.5 border-t border-slate-200 pt-8">
              {credentials.map((item) => (
                <li key={item} className={`flex items-start gap-3 ${type.bodySm}`}>
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1e6fcf]" />
                  <span className="font-semibold leading-snug">{item}</span>
                </li>
              ))}
              {about.branches && (
                <li className={`flex items-start gap-3 ${type.bodySm}`}>
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#1e6fcf]" />
                  <span className="font-semibold leading-snug">{about.branches} clients across India</span>
                </li>
              )}
            </ul>
            {about.brochureUrl && (
              <a
                href={about.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white"
              >
                Download Brochure
                <Download className="h-4 w-4" />
              </a>
            )}
          </aside>

          <div className="lg:col-span-8">
            <HtmlContent
              html={about.description}
              className={`${typeRichDescription} [&_p]:break-inside-avoid lg:columns-2 lg:gap-x-12`}
            />

            {getCompanyVideoEmbedUrl(about.introductionUrl) && (
              <div className="mt-10 border-t border-slate-200 pt-10 lg:mt-12 lg:pt-12">
                <CompanyVideoEmbed introductionUrl={about.introductionUrl} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const { data: about, isLoading, error } = useAbout({ refetchOnMount: "always" });
  const { data: teamMembers = [], isLoading: teamLoading } = useTeamMembers({
    refetchOnMount: "always",
  });
  const { banner: aboutBanner, isLoading: bannerLoading } = usePageBanner("about");

  if (error) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-slate-600">{error.message}</p>
        </div>
      </section>
    );
  }

  if (isLoading || !about) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center text-slate-500">Loading...</div>
      </section>
    );
  }

  const values = about.missions.map((m) => ({
    title: m.title,
    desc: m.description,
  }));

  return (
    <>
      <PageHeader
        title={aboutBanner?.title || "About Us"}
        breadcrumb="About"
        image={aboutBanner?.image}
        isLoading={bannerLoading}
      />

      <BrandStorySection about={about} />

      <StatsBar />

      {values.length > 0 && (
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <SectionTitle
              align="center"
              eyebrow="What Drives Us"
              title="Mission, Vision &amp; Values"
              subtitle="The principles that have guided every decision and every project for two decades."
            />
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {values.map((v) => (
                <MissionValueCard
                  key={v.title}
                  title={v.title}
                  desc={v.desc}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative py-16 md:py-24 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-[#e8f2fc]/50">
        <div className="pointer-events-none absolute inset-0 opacity-[0.35] bg-[radial-gradient(ellipse_at_top_right,_rgba(30,111,207,0.12),_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {about.founders?.length > 0 && (
              <div className="order-2 lg:order-1 mx-auto w-full max-w-md lg:max-w-none">
                <div className="grid grid-cols-2 gap-4 md:gap-5 justify-items-center">
                  {about.founders.map((founder) => (
                    <TeamMemberCard key={founder.id} member={toMemberCard(founder)} />
                  ))}
                </div>
              </div>
            )}

            <div className={`order-1 lg:order-2 ${about.founders?.length > 0 ? "" : "mx-auto max-w-3xl"}`}>
              <SectionTitle eyebrow="Leadership" title={about.title} />
              <div className="mt-5 sm:mt-6 border-l-4 border-[#1e6fcf] pl-4 sm:pl-6">
                <HtmlContent html={about.subTitle} className={typeRichDescription} />
              </div>

              <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-4">
                <LeadershipStat icon={Award} value={about.experience} label="Years" />
                <LeadershipStat icon={Building2} value={about.projects} label="Projects" />
                <LeadershipStat icon={Users} value={about.professionals} label="Team" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionTitle
            align="center"
            eyebrow="Our Team"
            title="Meet the People Behind SSA Techno"
            subtitle="A dedicated team of engineers, project leaders and specialists driving excellence across every project."
          />
          {teamLoading ? (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="max-w-[220px] w-full mx-auto rounded-lg border border-slate-200 overflow-hidden animate-pulse">
                  <div className="aspect-[3/4] bg-slate-200" />
                  <div className="p-2.5 space-y-1.5">
                    <div className="h-2.5 bg-slate-200 rounded w-2/3 mx-auto" />
                    <div className="h-2 bg-slate-100 rounded w-1/2 mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="mt-8 text-center py-16 bg-white rounded-xl border border-dashed border-slate-200">
              <Users className="w-12 h-12 text-[#1e6fcf]/40 mx-auto mb-4" />
              <p className="text-slate-600 font-semibold">No team members available yet.</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {teamMembers.map((member) => (
                <TeamMemberCard key={member.id} member={member} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Clients />
      <CtaBanner />
    </>
  );
}
